import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/cn';

const typographyVariants = cva('text-foreground', {
  variants: {
    variant: {
      h1: 'scroll-m-20 text-screen-title tracking-tight',
      h2: 'scroll-m-20 text-screen-subtitle tracking-tight',
      h3: 'scroll-m-20 text-section-title tracking-tight',
      h4: 'scroll-m-20 text-section-title tracking-tight',
      h5: 'scroll-m-20 text-card-title tracking-tight',
      h6: 'scroll-m-20 text-list-item-title tracking-tight',
      p: 'leading-7 [&:not(:first-child)]:mt-6',
      blockquote: 'mt-6 border-l-2 pl-6 italic',
      lead: 'text-progress-fact text-muted-foreground',
      large: 'text-card-title',
      small: 'text-form-label leading-none',
      muted: 'text-list-item-subtitle text-muted-foreground',
      inline: 'font-medium',
      subtle: 'text-list-item-subtitle text-muted-foreground',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
});

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: React.ElementType;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, ...props }, ref) => {
    const Component = React.useMemo(() => {
      if (as) return as;

      switch (variant) {
        case 'h1':
          return 'h1';
        case 'h2':
          return 'h2';
        case 'h3':
          return 'h3';
        case 'h4':
          return 'h4';
        case 'h5':
          return 'h5';
        case 'h6':
          return 'h6';
        case 'blockquote':
          return 'blockquote';
        default:
          return 'p';
      }
    }, [as, variant]);

    return (
      <Component
        ref={ref}
        className={cn(typographyVariants({ variant }), className)}
        {...props}
      />
    );
  }
);
Typography.displayName = 'Typography';

export { Typography, typographyVariants };
